/**
 * Encryption bridge — translates between in-memory record shapes and the
 * on-disk shapes that actually live in IndexedDB.
 *
 * Phase 3 of feature/offline-encryption. The crypto primitives come from
 * `./crypto.ts`; this module is purely the structural split:
 *
 *    in-memory record  ──split──►  { plaintext slice, encrypted_data blob }
 *    on-disk envelope  ──join───►  in-memory record
 *
 * `wrapForStorage<T>(entityType, record)` strips every field NOT listed in
 * PLAINTEXT_FIELDS[entityType] (or in SYNC_FIELDS_KEYS), JSON.stringify-and-
 * encrypts that PHI subset, and returns the on-disk envelope ready for
 * `db.<table>.put(envelope)`.
 *
 * `unwrapFromStorage<T>(entityType, envelope)` is the inverse — takes a row
 * fetched from IndexedDB, decrypts the blob, merges the PHI fields back onto
 * the plaintext slice, and returns the original record.
 *
 * Defensive properties:
 *   - Aadhaar / aadhar fields are explicitly forbidden from PLAINTEXT_FIELDS
 *     at runtime — a misconfiguration that put aadhaar in the indexed slice
 *     would throw before any record is written.
 *   - Round-trip is shape-preserving: unwrap(wrap(r)) deep-equals r.
 *   - Drugs are excluded — DrugRecord is not PHI and bypasses the bridge.
 *
 * No call site outside this file (and the helpers that use it) should hand-
 * craft an OnDisk envelope.
 */

import { encrypt, decrypt } from "./crypto";
import {
  PLAINTEXT_FIELDS,
  SYNC_FIELDS_KEYS,
} from "./db.types";
import type { EntityType, SyncQueueRecord } from "./db.types";
import { logger } from "../utils/logger";

const MIGRATION_FLAG_KEY = "kaagazy_v2_migration_done";

/**
 * Post-migration anomaly hook. After the v1 → v2 migration has completed
 * once on this device, no legitimate row should lack `encrypted_data`.
 * If the tolerant-unwrap path fires post-migration, that row is most
 * likely (a) a write from a build that bypassed wrapForStorage, or (b) a
 * directly-injected row (XSS / dev tools / browser extension). Log it
 * via the PHI-safe logger as a signal — don't throw, since that would
 * break the read path with no benefit (the threat model already grants
 * an XSS attacker full key access).
 */
function flagAnomaliesPostMigration(scope: string): void {
  try {
    if (localStorage.getItem(MIGRATION_FLAG_KEY) === "true") {
      logger.warn("encryption-bridge plaintext row after migration", { scope });
    }
  } catch {
    // localStorage unavailable — skip the warning.
  }
}

// ─── Aadhaar guard ───────────────────────────────────────────────────────────
//
// Run once at module load to catch misconfigured PLAINTEXT_FIELDS at startup
// rather than discovering them via a leaked PHI export later.

const AADHAAR_KEYS = ["aadhaar_number", "aadhar_number", "aadhaar", "aadhar"];

(function assertNoAadhaarInPlaintext() {
  for (const [entity, fields] of Object.entries(PLAINTEXT_FIELDS)) {
    for (const f of fields) {
      if (AADHAAR_KEYS.includes(f.toLowerCase())) {
        throw new Error(
          `encryption-bridge misconfigured: PLAINTEXT_FIELDS[${entity}] contains an aadhaar field "${f}". Aadhaar must always be encrypted.`,
        );
      }
    }
  }
})();

// ─── Entity record split ─────────────────────────────────────────────────────

function plaintextKeysFor(entityType: EntityType): readonly string[] {
  return PLAINTEXT_FIELDS[entityType];
}

/**
 * Split a record into (plaintext slice, PHI subset). Pure transform, no
 * crypto — used by both `wrapForStorage` and the test helpers.
 */
export function splitRecord<T extends Record<string, unknown>>(
  entityType: EntityType,
  record: T,
): { plaintext: Record<string, unknown>; phi: Record<string, unknown> } {
  const indexed = new Set<string>([
    ...plaintextKeysFor(entityType),
    ...SYNC_FIELDS_KEYS,
  ]);

  const plaintext: Record<string, unknown> = {};
  const phi:       Record<string, unknown> = {};

  for (const [key, value] of Object.entries(record)) {
    if (indexed.has(key)) {
      plaintext[key] = value;
    } else {
      phi[key] = value;
    }
  }
  return { plaintext, phi };
}

/**
 * Encrypt the PHI subset of a record and return the on-disk envelope.
 *
 * The returned object is structurally a `<Entity>OnDisk` for `entityType` —
 * callers cast it to the precise Dexie Table row type when calling `.put()`.
 */
export async function wrapForStorage<T extends Record<string, unknown>>(
  entityType: EntityType,
  record: T,
): Promise<Record<string, unknown>> {
  const { plaintext, phi } = splitRecord(entityType, record);
  const encrypted_data = await encrypt(JSON.stringify(phi));
  return { ...plaintext, encrypted_data };
}

/**
 * Decrypt the on-disk envelope back into a full in-memory record.
 *
 * The `T` type-parameter is what the caller asserts the joined shape will be
 * — there is no runtime check beyond JSON.parse, since the encrypted payload
 * is opaque until decrypted. Callers should match the entity type they are
 * fetching from.
 *
 * v1-tolerance: rows written before the encryption shape was introduced lack
 * an `encrypted_data` field. Rather than throwing during the transition
 * window, we pass them through as plaintext records — `./v2-migration.ts`
 * eventually rewrites every such row into the v2 envelope shape. Once the
 * migration has run on a device, no v1 rows remain.
 */
export async function unwrapFromStorage<T>(
  envelope: Record<string, unknown>,
): Promise<T> {
  const blob = envelope.encrypted_data;
  if (typeof blob !== "string" || blob.length === 0) {
    // v1 plaintext row — already decrypted by definition. The migration will
    // rewrite this into v2 shape on its next pass.
    flagAnomaliesPostMigration("entity");
    return envelope as T;
  }
  const { encrypted_data, ...plaintext } = envelope;
  void encrypted_data;
  const phiJson = await decrypt(blob);
  const phi     = JSON.parse(phiJson) as Record<string, unknown>;
  return { ...plaintext, ...phi } as T;
}

// ─── Sync-queue payload bridge ───────────────────────────────────────────────
//
// SyncQueueRecord.data is a generic `Record<string, unknown>` payload. The
// queue row's other fields stay plaintext (lifecycle / dedup / audit). Only
// `data` becomes an `encrypted_data` blob on disk.

export async function wrapSyncQueuePayload(
  record: Omit<SyncQueueRecord, "data"> & { data: Record<string, unknown> },
): Promise<Omit<SyncQueueRecord, "data"> & { encrypted_data: string }> {
  const { data, ...rest } = record;
  const encrypted_data = await encrypt(JSON.stringify(data));
  return { ...rest, encrypted_data };
}

export async function unwrapSyncQueuePayload(
  envelope: Record<string, unknown>,
): Promise<SyncQueueRecord> {
  const blob = envelope.encrypted_data;
  if (typeof blob !== "string" || blob.length === 0) {
    // v1 row — `data` was stored plaintext. Pass it through; the migration
    // rewrites it on its next pass.
    flagAnomaliesPostMigration("sync_queue");
    return envelope as unknown as SyncQueueRecord;
  }
  const { encrypted_data, ...rest } = envelope;
  void encrypted_data;
  const json = await decrypt(blob);
  const data = JSON.parse(json) as Record<string, unknown>;
  return { ...rest, data } as SyncQueueRecord;
}
